package com.banyan.flightviewer.web.state

import com.banyan.flightviewer.state.AppState
import com.banyan.flightviewer.state.EnrichedFlightData
import com.banyan.flightviewer.util.nowMs
import com.banyan.flightviewer.web.api.ServerClient
import com.banyan.flightviewer.web.platform.WebAlertManager
import kotlinx.browser.document
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

/**
 * Web port of the desktop monitoring loop in `desktop/ui/App.kt`.
 *
 * Differences from desktop:
 * - All upstream calls go through `:server` via [ServerClient] (no API keys
 *   in the browser).
 * - The query area uses settings lat/lon/radiusKm directly. Phase 5 will
 *   wire the actual Leaflet viewport once the map exists.
 * - Pauses while the tab is hidden (saves upstream credits).
 * - Pixoo, alerts, and map marker updates are not yet wired up.
 */
class WebMonitoringLoop(
    private val appState: AppState,
    private val serverClient: ServerClient,
    private val uiState: WebUiState,
    private val alerts: WebAlertManager? = null,
) {
    private var job: Job? = null
    private var lastEnrichmentTime = 0L

    fun start() {
        job?.cancel()
        appState.setMonitoring(true)
        appState.setStatusMessage("Monitoring started")

        job = appState.scope.launch {
            var consecutiveErrors = 0
            while (isActive) {
                // Pause when the tab is hidden -- saves upstream credits.
                // visibilityState isn't on the legacy w3c Document type, so use
                // dynamic dispatch to read it.
                val hidden = (document.asDynamic().visibilityState as? String) == "hidden"
                if (hidden) {
                    delay(2_000)
                    continue
                }

                try {
                    val s = appState.settings.value

                    val source = when (s.apiSource) {
                        "Airplanes.live" -> "live"
                        else -> "adsbx"
                    }

                    // Query area: prefer the map's current viewport so
                    // panning/zooming actually refreshes the shown aircraft.
                    // Fall back to home settings when the map hasn't reported
                    // bounds yet (first few seconds after launch).
                    val area = uiState.mapQueryArea.value
                    val queryLat = area?.lat ?: s.lat
                    val queryLon = area?.lon ?: s.lon
                    val queryRadius = area?.radiusKm ?: s.radiusKm.toDouble()

                    val aircraft = serverClient.getAircraft(
                        lat = queryLat,
                        lon = queryLon,
                        radiusKm = queryRadius,
                        source = source,
                    )

                    // Pre-cache type + registration from the position data
                    // (these come back free from ADSB / Airplanes.live).
                    val cache = appState.cachedEnrichedData.value
                    for (ac in aircraft) {
                        if (ac.hex !in cache &&
                            (ac.aircraftType != "Unknown" || ac.registration.isNotBlank())
                        ) {
                            appState.cacheEnrichedData(
                                ac.hex,
                                EnrichedFlightData(
                                    aircraftType = ac.aircraftType.takeIf { it != "Unknown" } ?: "",
                                    registration = ac.registration,
                                ),
                            )
                        }
                    }

                    // FR24 bulk enrichment
                    val latestCache = appState.cachedEnrichedData.value
                    if (s.fr24BulkEnrichment) {
                        val uncached = aircraft.filter { ac ->
                            val cached = latestCache[ac.hex]
                            cached == null ||
                                (cached.origin.isBlank() && cached.destination.isBlank() &&
                                    cached.origin != "_none")
                        }
                        val cooldownMs = s.enrichmentCooldownSec * 1000L
                        val cooldownElapsed = nowMs() - lastEnrichmentTime > cooldownMs

                        if (uncached.isNotEmpty() && cooldownElapsed) {
                            val batchAircraft = uncached.take(15)
                            val batchCallsigns = batchAircraft.map { it.callsign }
                            val enrichResult = serverClient.bulkEnrich(batchCallsigns)
                            lastEnrichmentTime = nowMs()
                            if (enrichResult != null) {
                                for (ac in batchAircraft) {
                                    val enriched = enrichResult.byCallsign[ac.callsign]
                                        ?: enrichResult.byHex[ac.hex.lowercase().trim()]
                                    if (enriched != null) {
                                        appState.cacheEnrichedData(ac.hex, enriched)
                                    } else {
                                        appState.cacheEnrichedData(
                                            ac.hex,
                                            EnrichedFlightData(origin = "_none"),
                                        )
                                    }
                                }
                            }
                        }
                    }

                    // Apply cached enrichment to the aircraft list
                    val finalCache = appState.cachedEnrichedData.value
                    val enrichedAircraft = aircraft.map { ac ->
                        val e = finalCache[ac.hex]
                        if (e != null) ac.copy(
                            origin = e.origin.takeIf { it != "_none" }?.ifEmpty { null }
                                ?: ac.origin,
                            destination = e.destination.ifEmpty { ac.destination },
                            aircraftType = e.aircraftType.ifEmpty { ac.aircraftType },
                            registration = e.registration.ifEmpty { ac.registration },
                            fr24Id = e.fr24Id.ifEmpty { ac.fr24Id },
                        ) else ac
                    }

                    appState.updateAircraftList(enrichedAircraft)
                    appState.setLastUpdateTime(currentTimeOfDay())
                    appState.setStatusMessage("${enrichedAircraft.size} aircraft in range")
                    consecutiveErrors = 0

                    alerts?.check(enrichedAircraft)
                    appState.evictStaleEnrichmentData()

                    // Wait for next cycle, or wake early if requested
                    withTimeoutOrNull(s.updateIntervalSec * 1000L) {
                        appState.immediateRefreshSignal.first()
                    }
                } catch (e: CancellationException) {
                    throw e
                } catch (e: Throwable) {
                    consecutiveErrors++
                    val backoffMs = minOf(consecutiveErrors * 5_000L, 60_000L)
                    appState.setStatusMessage("Server error: ${e.message ?: "unknown"}")
                    delay(backoffMs)
                }
            }
        }
    }

    fun stop() {
        job?.cancel()
        job = null
        appState.setMonitoring(false)
        appState.setStatusMessage("Monitoring stopped")
    }

    private fun currentTimeOfDay(): String {
        val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
        val h = now.hour.toString().padStart(2, '0')
        val m = now.minute.toString().padStart(2, '0')
        val s = now.second.toString().padStart(2, '0')
        return "$h:$m:$s"
    }
}
